/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { isEqual, get, reduce, map } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AsyncSelect from 'react-select/async';

import Filter from '../../components/Filters/Filter';
import {
  decodeCountriesForFilter,
  decodeDate,
  decodePages,
  encodePages,
  encodeAllowedCategories
} from './config';
import countryOptions from '../../config/filterOptions/country';

import './Modal.css';

const AdminModal = ({ isOpen, closeModal, user, onSubmit, orgs, userRole }) => {
  const [form, setForm] = useState({});
  const [additionalUsers, setAdditionalUsers] = useState([]);
  const [failMessage, setFailMessage] = useState(null);
  const [org, setOrg] = useState(null);
  const [countriesVal, setCountriesVal] = useState(null);
  const [dateVal, setDateVal] = useState(null);
  const [bulkEditing, setBulkEditing] = useState(false);

  const formatValue = key => form[key] || '';

  const onChangeValue = key => e => {
    let { value } = e.target;
    value = key === 'email' ? value.toLowerCase().trim() : value;
    const u = { ...form, [key]: value };
    setForm(u);
  };

  const handleDateChange = e => {
    setDateVal(e.target.value);
    const allowedDataCategories = encodeAllowedCategories(
      countriesVal && countriesVal.length > 0 ? countriesVal : false,
      e.target.value
    );

    setForm({
      ...form,
      ...{ lookerUserInfo: { ...form.lookerUserInfo, allowedDataCategories } }
    });
  };

  const handleDropdownChange = (id, v) => {
    if (id === 'allowed-pages') {
      const { pages: allowedPages = [], dataCategories = [] } = encodePages(
        v ? v.map(f => f.label) : []
      );

      const dataCategoriesMap = reduce(
        dataCategories,
        (memo, category) => {
          const [key, value] = category.split(':');
          if (key && value) {
            memo[key] = value;
          }
          return memo;
        },
        {}
      );

      const allowedDataCategories = map(
        form.lookerUserInfo.allowedDataCategories,
        allowedCategory => {
          const [key] = allowedCategory.split(':');
          const updatedCategoryValue = key ? dataCategoriesMap[key] : undefined;
          return updatedCategoryValue
            ? `${key}:${updatedCategoryValue}`
            : allowedCategory;
        }
      );

      if (bulkEditing && !v) {
        return setForm({
          ...form,
          ...{
            lookerUserInfo: {
              ...form.lookerUserInfo,
              allowedDataCategories,
              allowedPages: undefined
            }
          }
        });
      }

      setForm({
        ...form,
        ...{
          lookerUserInfo: {
            ...form.lookerUserInfo,
            allowedDataCategories,
            allowedPages
          }
        }
      });
    } else if (id === 'allowed-countries') {
      setCountriesVal(v ? v.map(f => f.label) : []);
      const allowedDataCategories = encodeAllowedCategories(
        v ? v.map(f => f.label) : false,
        dateVal
      );

      setForm({
        ...form,
        ...{ lookerUserInfo: { ...form.lookerUserInfo, allowedDataCategories } }
      });
    } else if (id === 'user-status') {
      if (bulkEditing && !v) {
        return setForm({
          form
        });
      }

      setForm({ ...form, disabled: get(v, 'value', false) });
    }
  };

  const onClickAddUserToGroup = () => {
    setAdditionalUsers([...additionalUsers, {}]);
  };

  const removeAdditionalUser = index => () => {
    const au = [...additionalUsers];
    au.splice(index, 1);

    setAdditionalUsers(au);
  };

  const additionalUserRow = (u, index) => {
    const updateUser = label => e => {
      const value =
        label === 'email'
          ? e.target.value.toLowerCase().trim()
          : e.target.value;
      const au = [...additionalUsers];
      au[index] = { ...u, [label]: value };
      setAdditionalUsers(au);
    };

    return (
      <>
        <div className="cvmi-admin__additional-user-form-first">
          <div className="cvmi-admin__additional-user-form-label">First</div>
          <input
            className="cvmi-admin__edit-user-text-input"
            value={u.username}
            onChange={updateUser('username')}
          />
        </div>
        <div className="cvmi-admin__additional-user-form-last">
          <div className="cvmi-admin__additional-user-form-label">Last</div>
          <input
            className="cvmi-admin__edit-user-text-input"
            value={u.lastname}
            onChange={updateUser('lastname')}
          />
        </div>
        <div className="cvmi-admin__additional-user-form-email">
          <div className="cvmi-admin__additional-user-form-label">Email</div>
          <input
            className="cvmi-admin__edit-user-text-input"
            value={u.email}
            onChange={updateUser('email')}
          />
        </div>
      </>
    );
  };

  const formatStatus = isDisabled => {
    if (!isDisabled) {
      return { value: false, label: 'Active' };
    }

    return { value: true, label: 'Disabled' };
  };

  const disableSubmit = () =>
    !form.email ||
    !form.username ||
    !form.lookerUserInfo ||
    (isEqual(user, form) && additionalUsers.length < 1) ||
    userRole !== 'admin';

  const disableBulkSubmit = () => {
    let disable = true;
    if (Object.keys(form).length < 1) {
      return disable;
    }

    const isValid = f => {
      Object.keys(f).forEach(k => {
        if (typeof f[k] === 'object' && f[k]) {
          isValid(f[k]);
        } else if (f[k]) {
          disable = false;
        }
      });
    };

    isValid(form);

    return disable;
  };

  const onClickSubmit = () => {
    let formmattedUsers;
    if (bulkEditing) {
      formmattedUsers = user.map(u => ({
        ...u,
        ...form,
        lookerUserInfo: { ...u.lookerUserInfo, ...form.lookerUserInfo }
      }));
    } else {
      formmattedUsers = [
        ...additionalUsers.map(u => ({
          ...form,
          ...u,
          uuid: null,
          userId: null
        }))
      ];
      if ((user && !isEqual(user, form)) || !user) {
        formmattedUsers.push(form);
      }

      if (!form.organizationName) {
        // eslint-disable-next-line no-alert
        alert(
          'You just created a user with tied to no Organization. While they will be able to use the app, we will not be able to track their usage of the app. Please report the creation of these user(s) to the CVMi tech team'
        );
      }
    }

    const success = onSubmit(formmattedUsers);
    if (success) {
      setFailMessage(null);
      setForm({});
      setOrg(null);
      setDateVal(null);
      setCountriesVal(null);
      closeModal();
    } else {
      setFailMessage('update failed! Try again or contact a team member!');
    }
  };

  const onCloseModal = () => {
    setFailMessage(null);
    setForm({});
    setOrg(null);
    setDateVal(null);
    setCountriesVal(null);
    setAdditionalUsers([]);
    closeModal();
  };

  const onSelectOrg = ({ label, value }) => {
    setOrg({ label, value });
    const u = { ...form, organizationName: label };
    setForm(u);
  };

  const filterOrgs = inputValue =>
    orgs.filter(i => i.label.toLowerCase().includes(inputValue.toLowerCase()));

  const promiseOptions = inputValue =>
    new Promise(resolve => {
      resolve(filterOrgs(inputValue));
    });

  useEffect(() => {
    if (Array.isArray(user)) {
      setForm({});
      setBulkEditing(true);
      return;
    }

    setBulkEditing(false);
    if (user) {
      setForm(user);
      const userOrg = orgs.filter(o => o.label === user.organizationName)[0];
      const allowedDate = decodeDate(user.lookerUserInfo);
      if (userOrg) {
        setOrg(userOrg);
      }
      if (allowedDate) {
        setDateVal(allowedDate);
      }
    } else {
      setForm({});
      setOrg(null);
      setDateVal(null);
      setCountriesVal(null);
    }
  }, [user]);

  return (
    <Modal
      className="cvmi-admin__edit-user-modal"
      id="cvmi-admin__edit-user-modal"
      show={isOpen}
      onHide={onCloseModal}
      backdrop
    >
      {bulkEditing && user ? (
        <div>
          <div className="cvmi-admin__edit-user-modal-row">
            <h1>Bulk Edit</h1>
          </div>
          <div>
            <h4>
              Not that any field changed below will OVERWRITE that field for
              each user listed below. Any field left blank will be ignored and
              unchanged.
            </h4>
          </div>
          <div>
            <h4>
              Users:{' '}
              {user.map((u, i) => (
                <>
                  <span key={`bulk-edit${u.uuid}`}>{u.email}</span>
                  {i === user.length - 1 ? null : ', '}
                </>
              ))}
            </h4>
          </div>
          <div>
            <div className="cvmi-admin__edit-user-modal-row">
              <div className="cvmi-admin__edit-user-modal-input-column">
                <div className="cvmi-admin__edit-user-modal-input-wrapper">
                  <div className="cvmi-admin__edit-user-modal-label">
                    Organization{' '}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="admin-modal__item-tooltip">
                          <div>
                            If you cannot find the organization, leave this
                            blank.
                          </div>
                          <div>
                            Then notify cvmi team. Dev support is needed to
                            update default.yaml file and create looker user for
                            new org.
                          </div>
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        className="benchmark-info-icon"
                        size="1x"
                        icon="info-circle"
                      />
                    </OverlayTrigger>
                  </div>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={promiseOptions}
                    value={org}
                    onChange={onSelectOrg}
                  />
                </div>
              </div>
            </div>
            <div className="cvmi-admin__edit-user-modal-row">
              <div className="cvmi-admin__edit-user-modal-input-column">
                <div className="cvmi-admin__edit-user-modal-input-wrapper">
                  <div className="cvmi-admin__edit-user-modal-label">
                    Allowed Pages
                  </div>
                  <Filter
                    className="cvmi-admin__permissions-dropdown"
                    hasChanged={false}
                    isMulti
                    onChange={handleDropdownChange}
                    showInlineSelections
                    disallowExcluded
                    id="allowed-pages"
                    options={[
                      { label: 'Movies', value: 'Movies', id: 'allowed-pages' },
                      { label: 'Shows', value: 'Shows', id: 'allowed-pages' }
                    ]}
                    value={decodePages(form.lookerUserInfo).map(v => ({
                      label: v,
                      value: v
                    }))}
                    hasBoolean={false}
                  />
                </div>
              </div>
            </div>
            <div className="cvmi-admin__edit-user-modal-row">
              <div className="cvmi-admin__edit-user-modal-input-column">
                <div className="cvmi-admin__edit-user-modal-input-wrapper">
                  <div className="cvmi-admin__edit-user-modal-label">
                    Country Permissions
                  </div>
                  <Filter
                    className="cvmi-admin__permissions-dropdown"
                    isMulti
                    onChange={handleDropdownChange}
                    showInlineSelections
                    disallowExcluded
                    id="allowed-countries"
                    options={[
                      { value: 'Global', label: 'Global' },
                      ...countryOptions
                    ]}
                    value={
                      form.lookerUserInfo
                        ? decodeCountriesForFilter(form.lookerUserInfo)
                        : []
                    }
                  />
                </div>
              </div>
            </div>
            <div className="cvmi-admin__edit-user-modal-row">
              <div className="cvmi-admin__edit-user-modal-input-column">
                <div className="cvmi-admin__edit-user-modal-input-wrapper">
                  <div className="cvmi-admin__edit-user-modal-label">
                    Historical Data Access Rolling Window
                  </div>
                  <div className="cvmi-admin__data-access-filter-wrapper">
                    last{' '}
                    <input
                      className="cvmi-admin__data-access-filter"
                      type="number"
                      min="0"
                      value={decodeDate(form.lookerUserInfo)}
                      onChange={handleDateChange}
                    />{' '}
                    months (leave blank or 0 for all-time access)
                  </div>
                </div>
              </div>
            </div>
            <div className="cvmi-admin__edit-user-modal-row">
              <div className="cvmi-admin__edit-user-modal-input-column">
                <div className="cvmi-admin__edit-user-modal-input-wrapper">
                  <div className="cvmi-admin__edit-user-modal-label">
                    Status
                  </div>
                  <Filter
                    className="cvmi-admin__permissions-dropdown"
                    onChange={handleDropdownChange}
                    showInlineSelections
                    id="user-status"
                    options={[
                      { value: true, label: 'Disabled' },
                      { value: false, label: 'Active' }
                    ]}
                    value={formatStatus(form.disabled)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="cvmi-admin__modal-actions-row">
            <div />
            <button
              className="cvmi-admin__edit-user-submit"
              disabled={disableBulkSubmit() || userRole !== 'admin'}
              onClick={onClickSubmit}
            >
              submit
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="cvmi-admin__edit-user-modal-row">
            <h1>{user ? 'Edit User' : 'Add New User'}</h1>
          </div>
          <div className="cvmi-admin__edit-user-modal-row">
            <div className="cvmi-admin__edit-user-modal-input-column">
              <div className="cvmi-admin__edit-user-modal-input-wrapper">
                <div className="cvmi-admin__edit-user-modal-label">
                  First Name
                </div>
                <input
                  className="cvmi-admin__edit-user-text-input"
                  value={formatValue('username')}
                  onChange={onChangeValue('username')}
                />
              </div>
            </div>
            <div className="cvmi-admin__edit-user-modal-input-column">
              <div className="cvmi-admin__edit-user-modal-input-wrapper">
                <div className="cvmi-admin__edit-user-modal-label">
                  Last Name
                </div>
                <input
                  className="cvmi-admin__edit-user-text-input"
                  value={formatValue('lastname')}
                  onChange={onChangeValue('lastname')}
                />
              </div>
            </div>
          </div>
          <div className="cvmi-admin__edit-user-modal-row">
            <div className="cvmi-admin__edit-user-modal-input-column">
              <div className="cvmi-admin__edit-user-modal-input-wrapper">
                <div className="cvmi-admin__edit-user-modal-label">Email</div>
                <input
                  className="cvmi-admin__edit-user-text-input"
                  value={formatValue('email')}
                  onChange={onChangeValue('email')}
                />
              </div>
            </div>
          </div>
          <div className="cvmi-admin__edit-user-modal-row">
            <div className="cvmi-admin__edit-user-modal-input-column">
              <div className="cvmi-admin__edit-user-modal-input-wrapper">
                <div className="cvmi-admin__edit-user-modal-label">
                  Organization{' '}
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="admin-modal__item-tooltip">
                        <div>
                          If you cannot find the organization, leave this blank.
                        </div>
                        <div>
                          Then notify cvmi team. Dev support is needed to update
                          default.yaml file and create looker user for new org.
                        </div>
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon
                      className="benchmark-info-icon"
                      size="1x"
                      icon="info-circle"
                    />
                  </OverlayTrigger>
                </div>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={promiseOptions}
                  value={org}
                  onChange={onSelectOrg}
                />
              </div>
            </div>
          </div>
          <div className="cvmi-admin__edit-user-modal-row">
            <div className="cvmi-admin__edit-user-modal-input-column">
              <div className="cvmi-admin__edit-user-modal-input-wrapper">
                <div className="cvmi-admin__edit-user-modal-label">
                  Allowed Pages
                </div>
                <Filter
                  className="cvmi-admin__permissions-dropdown"
                  hasChanged={false}
                  isMulti
                  onChange={handleDropdownChange}
                  showInlineSelections
                  id="allowed-pages"
                  options={[
                    { label: 'Movies', value: 'Movies', id: 'allowed-pages' },
                    { label: 'Shows', value: 'Shows', id: 'allowed-pages' }
                  ]}
                  value={decodePages(form.lookerUserInfo).map(v => ({
                    label: v,
                    value: v
                  }))}
                  hasBoolean={false}
                />
              </div>
            </div>
          </div>
          <div className="cvmi-admin__edit-user-modal-row">
            <div className="cvmi-admin__edit-user-modal-input-column">
              <div className="cvmi-admin__edit-user-modal-input-wrapper">
                <div className="cvmi-admin__edit-user-modal-label">
                  Country Permissions
                </div>
                <Filter
                  className="cvmi-admin__permissions-dropdown"
                  isMulti
                  onChange={handleDropdownChange}
                  showInlineSelections
                  id="allowed-countries"
                  options={[
                    { value: 'Global', label: 'Global' },
                    ...countryOptions
                  ]}
                  value={
                    form.lookerUserInfo
                      ? decodeCountriesForFilter(form.lookerUserInfo)
                      : []
                  }
                />
              </div>
            </div>
          </div>
          <div className="cvmi-admin__edit-user-modal-row">
            <div className="cvmi-admin__edit-user-modal-input-column">
              <div className="cvmi-admin__edit-user-modal-input-wrapper">
                <div className="cvmi-admin__edit-user-modal-label">
                  Historical Data Access Rolling Window
                </div>
                <div className="cvmi-admin__data-access-filter-wrapper">
                  last{' '}
                  <input
                    className="cvmi-admin__data-access-filter"
                    type="number"
                    min="0"
                    value={decodeDate(form.lookerUserInfo)}
                    onChange={handleDateChange}
                  />{' '}
                  months (leave blank or 0 for all-time access)
                </div>
              </div>
            </div>
          </div>
          <div className="cvmi-admin__edit-user-modal-row">
            <div className="cvmi-admin__edit-user-modal-input-column">
              <div className="cvmi-admin__edit-user-modal-input-wrapper">
                <div className="cvmi-admin__edit-user-modal-label">Status</div>
                <Filter
                  className="cvmi-admin__permissions-dropdown"
                  onChange={handleDropdownChange}
                  showInlineSelections
                  id="user-status"
                  options={[
                    { value: true, label: 'Disabled' },
                    { value: false, label: 'Active' }
                  ]}
                  value={formatStatus(form.disabled)}
                />
              </div>
            </div>
          </div>

          {additionalUsers.length > 0 ? (
            <div>
              {additionalUsers.map((u, index) => (
                <div className="cvmi-admin__new-user-form-additional-user">
                  <button
                    className="cvmi-admin__new-user-form-remove-additional-user-button"
                    onClick={removeAdditionalUser(index)}
                  >
                    <FontAwesomeIcon
                      className="cvmi-admin__icon"
                      icon="times"
                      size="1x"
                    />
                  </button>
                  {additionalUserRow(u, index)}
                </div>
              ))}
            </div>
          ) : null}

          <div className="cvmi-admin__edit-failed-message">{failMessage}</div>

          <div className="cvmi-admin__modal-actions-row">
            <button
              className="cvmi-admin__edit-user-add-org-user"
              onClick={onClickAddUserToGroup}
            >
              <FontAwesomeIcon
                className="cvmi-admin__icon"
                icon="plus"
                size="1x"
              />{' '}
              Add Organization User{' '}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="admin-modal__item-tooltip">
                    <div>
                      Add another user with same permissions, pages, &
                      organization info.
                    </div>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon
                  className="benchmark-info-icon"
                  size="1x"
                  icon="info-circle"
                />
              </OverlayTrigger>
            </button>
            <button
              className="cvmi-admin__edit-user-submit"
              disabled={disableSubmit()}
              onClick={onClickSubmit}
            >
              submit
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AdminModal;
